"use client";

import type { useChat } from "@ai-sdk/react";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { useParams } from "next/navigation";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { OrderList } from "../shared/order-list";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import { UPDATE_QUERY_GROUP_ORDER_NAME } from "./constants";
import type { UpdateQueryGroupOrderInput } from "./schema";

const SaQueryGroupsQuery = graphql(`
  query SaQueryGroupOrderConfirm($projectId: ID!) {
    node(id: $projectId) {
      __typename
      ... on SearchAnalyticsProject {
        id
        name
        searchAnalytics {
          id
          queryGroups {
            id
            name
          }
        }
      }
    }
  }
`);

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

export function UpdateQueryGroupOrderConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const { i18n } = useLingui();
  const params = useParams();
  const lang = typeof params.lang === "string" ? params.lang : i18n.locale;
  const workspaceId =
    typeof params.workspaceId === "string"
      ? decodeURIComponent(params.workspaceId)
      : "";
  const isTarget =
    isToolUIPart(part) && getToolName(part) === UPDATE_QUERY_GROUP_ORDER_NAME;
  const isInputAvailable = part.state === "input-available";

  const input = (part.input ?? {}) as Partial<UpdateQueryGroupOrderInput>;
  const projectId = input.projectId ?? "";
  const initialOrderIds = input.ids ?? [];
  const [orderIds, setOrderIds] = useState<string[]>(initialOrderIds);

  const [{ data, fetching }] = useQuery({
    query: SaQueryGroupsQuery,
    variables: { projectId },
    requestPolicy: "network-only",
    pause: !projectId,
  });

  const projectNode =
    data?.node?.__typename === "SearchAnalyticsProject" ? data.node : undefined;
  const currentGroups =
    projectNode?.searchAnalytics?.queryGroups?.filter(
      (g): g is NonNullable<typeof g> => !!g,
    ) ?? [];

  const currentMap = new Map(currentGroups.map((g) => [g?.id ?? "", g?.name]));
  const currentOrder = currentGroups
    .map((g) => g?.id)
    .filter((id): id is string => Boolean(id));

  useEffect(() => {
    if (!orderIds.length && currentOrder.length) {
      setOrderIds(currentOrder);
    }
  }, [currentOrder, orderIds.length]);

  const displayOrder = orderIds.length
    ? Array.from(new Set(orderIds))
    : currentOrder;

  const shouldRender = isTarget && isInputAvailable;

  if (!shouldRender) return null;

  return (
    <div className="p-4 text-sm space-y-3">
      <OrderList
        title={
          <span className="text-sm md:text-base">
            <Trans>
              Do you want to update this Search Analytics query group order?
            </Trans>
          </span>
        }
        badges={
          <>
            <Badge variant="outline" className="mr-2">
              <Trans>Query Groups</Trans>
            </Badge>
            {fetching ? (
              <Skeleton className="inline-block h-3 w-40 align-middle" />
            ) : projectNode ? (
              <a
                className="text-primary underline"
                href={
                  workspaceId
                    ? `/${encodeURIComponent(lang)}/${encodeURIComponent(
                        workspaceId,
                      )}/sa/${encodeURIComponent(projectNode.id)}/settings`
                    : "#"
                }
              >
                {projectNode.name}
              </a>
            ) : (
              <span className="font-medium text-muted-foreground">
                <Trans>Project name unavailable</Trans>
              </span>
            )}
          </>
        }
        items={currentOrder.map((id) => ({
          id,
          label: currentMap.get(id) ?? <Trans>Unknown</Trans>,
        }))}
        order={displayOrder}
        loading={fetching}
        fallbackLabel={i18n._("No new order was provided")}
        onChange={setOrderIds}
        maxHeight="24rem"
      />
      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={UPDATE_QUERY_GROUP_ORDER_NAME}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
          overrideInput={{ ...input, ids: displayOrder }}
        >
          <Trans>Update</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={UPDATE_QUERY_GROUP_ORDER_NAME}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
